import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { CommandPalette } from "./CommandPalette";
import { useOpenRiskCases, usePendingInferenceRequests, usePendingQuotaRequests, useSupportCounts } from "../lib/queries";
import { Icon, OpsMark, type IconName } from "./icons";
import { Button, PlatformRoleBadge, cx } from "./ui";

/*
 * Grouped by subject, in groups small enough to scan.
 *
 * A flat run of nine made Plans and Deployments look like siblings, so the rail
 * gained groups — and then "Platform" quietly became where everything went that
 * did not obviously belong elsewhere. It held six of the fourteen items and
 * spanned four unrelated subjects: the fleet, what customers are being told,
 * what the platform charges, and how much it earned. A group that needs a
 * comment on every second item explaining why it is there is not a group.
 *
 * What replaced it is six groups of two to four. The test each one has to
 * pass is that its title predicts its contents: somebody looking for the
 * inference margin should not have to know that it was filed next to servers.
 *
 * Money is together now. Plans and Promotions sat at the top of the rail while
 * Revenue sat alone at the bottom, so what the platform charges and what it
 * actually earned were as far apart as the rail allows.
 *
 * Inference keeps its own group rather than joining Commerce, because the
 * catalogue and its margin report are used in one motion: the action AI usage
 * leads to — moving a markup, unlisting a model — is taken on the page above it.
 *
 * Overview stays ungrouped at the top. It is the way in rather than a category
 * anybody browses, and a heading over a single item is noise.
 */
export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}

export const NAV_GROUPS: { title: string | null; items: NavItem[] }[] = [
  { title: null, items: [{ to: "/", label: "Overview", icon: "overview", end: true }] },
  {
    // What is running right now, and what is being pushed onto it. The two
    // pages an operator opens when something is wrong.
    title: "Fleet",
    items: [
      { to: "/servers", label: "Servers", icon: "servers" },
      { to: "/deployments", label: "Deployments", icon: "deployments" },
    ],
  },
  {
    // The cross-tenant registries of who is on the platform and what they
    // hold. Each is somewhere an operator arrives by searching for a name they
    // were given.
    title: "Tenancy",
    items: [
      /*
       * The queue leads the group. It is the one destination here an operator
       * opens because somebody is waiting rather than because they went
       * looking, and the other two are usually where a ticket sends you next —
       * which is the argument for it being in this group at all: support is
       * about the tenants, and it sits with the pages that describe them.
       */
      { to: "/support", label: "Support", icon: "support" },
      { to: "/organizations", label: "Organizations", icon: "orgs" },
      { to: "/users", label: "Users", icon: "users" },
      /*
       * The names tenants have registered through the platform. A registry
       * like the two above it — the ticket says "my domain", and this is
       * where that name is looked up — and not a workload: nothing runs, and
       * what is billed is a purchase rather than an hour. The catalogue and
       * its margins are on the same page, because the operator who is asked
       * why a renewal cost what it did is the one who sets the markup.
       */
      { to: "/domains", label: "Domains", icon: "globe" },
    ],
  },
  {
    /*
     * The two things tenants actually run, and the two the platform bills for.
     * They were not siblings in the rail until databases had a page of their
     * own: apps sat with the tenant registries and databases were not in the
     * console at all, so the half of the product that stores data was invisible
     * to the people operating it.
     */
    title: "Workloads",
    items: [
      { to: "/apps", label: "Apps", icon: "apps" },
      { to: "/databases", label: "Databases", icon: "databases" },
      /* A scheduled job is a tenant workload, not a setting: it is created,
         it runs on a server, and it is billed. Commerce would be the other
         reading, and that group is already at the four this rail allows. */
      { to: "/schedules", label: "Schedules", icon: "clock" },
      /* Also a tenant workload: created, running on a server, and billed. It
         belongs here rather than under Servers, where the tunnel container
         happens to run — the tenant owns it, the fleet only hosts it. */
      { to: "/tunnels", label: "Tunnels", icon: "lock" },
    ],
  },
  {
    /*
     * Earned first, then the things that produce it: a report is read far more
     * often than a catalogue is edited.
     *
     * Announcements closes the group rather than sitting with the logs, because
     * a campaign and the notice that advertises it are one motion — the
     * promotions page already tracks which campaign the public site is running,
     * and this is where that gets written. The cost is that maintenance and
     * incident notices, the other half of what this page publishes, are now
     * filed under commerce; the search palette is the way to those.
     *
     * Four items is the most this rail allows a group, so anything else
     * commercial needs a rethink here rather than another row.
     */
    title: "Commerce",
    items: [
      { to: "/revenue", label: "Revenue", icon: "revenue" },
      { to: "/plans", label: "Plans", icon: "plans" },
      { to: "/promotions", label: "Promotions", icon: "promotions" },
      { to: "/announcements", label: "Announcements", icon: "announcements" },
    ],
  },
  {
    /*
     * "Inference" rather than "AI", which is what this read at first and what
     * the rail could not carry: two letters under 0.14em tracking render as a
     * stray mark beside COMMERCE and TENANCY rather than as a heading. It is
     * also the more precise word, and the one the docs and the public site
     * already use for this product.
     */
    title: "Inference",
    items: [
      { to: "/ai-models", label: "AI models", icon: "models" },
      { to: "/ai-usage", label: "AI usage", icon: "trend" },
      /*
       * The giveaway, filed with the catalogue it draws its models from and the
       * report its cost lands in — the same one-motion argument that grouped
       * those two. It reports like a promotion and is deliberately not filed
       * with them: the rail's rule is that a title predicts its contents, and
       * somebody looking for the free-AI queue looks under Inference.
       *
       * Last in the group because it is the only one here opened because
       * somebody is waiting, which is what the badge is for.
       */
      { to: "/free-inference", label: "Free inference", icon: "key" },
    ],
  },
  {
    // Who gets in and who gets looked at. The queue of organizations whose
    // activity looks like abuse, and the door-list registration checks an
    // address against. Its own group because neither is a tenant, a
    // workload or a record: both are decisions about people, made here.
    title: "Safety",
    items: [
      { to: "/safety", label: "Cases", icon: "shield", end: true },
      { to: "/safety/monitor", label: "Monitor", icon: "refresh" },
      { to: "/email-domains", label: "Email domains", icon: "eye" },
    ],
  },
  {
    // What the platform sent and did. Both are read to answer a question about
    // something that already happened, which is what separates them from the
    // authoring surface that produces some of it.
    title: "Records",
    items: [
      { to: "/emails", label: "Email log", icon: "mail" },
      { to: "/audit", label: "Audit log", icon: "audit" },
    ],
  },
];

/**
 * The navigation itself, separated from the chrome around it.
 *
 * A component rather than JSX inside `Layout` so the rail can be rendered on
 * its own: by a test, and by anybody wanting to look at how the groups read
 * without signing into the console first. Grouping is the kind of change that
 * is judged by looking at it, and the alternative was judging it from a diff.
 */
/**
 * `badges` is keyed by destination, and only the support queue uses one.
 *
 * Passed in rather than fetched here, so the rail stays a pure component that
 * a test — or anybody wanting to look at the grouping — can render without a
 * query client or a signed-in session behind it.
 */
export function NavRail({ badges = {} }: { badges?: Record<string, number | undefined> }) {
  return (
      <nav className="flex-1 space-y-6">
        {NAV_GROUPS.map((group, index) => (
          <div key={group.title ?? `group-${index}`}>
            {group.title && <p className="micro px-3 pb-2">{group.title}</p>}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cx(
                        /* A left rule rather than the filled pill this used to
                           be, matching the customer console: square geometry,
                           and it reads as a position in a list rather than a
                           selected object. The pill also carried a second bar
                           beside it, so the current page was marked twice. */
                        "flex items-center gap-3 border-l-2 py-2 pl-3 text-sm transition-colors duration-200 ease-smooth pointer-coarse:min-h-11",
                        isActive
                          ? "border-accent bg-accent-soft/40 text-fg"
                          : "border-transparent text-fg-muted hover:border-line-strong hover:text-fg",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon
                          name={item.icon}
                          className={cx("size-4", isActive ? "text-accent" : "text-fg-subtle")}
                        />
                        {item.label}
                        {/* Unanswered tickets, from wherever in the console the
                            operator happens to be. Zero renders nothing: a
                            badge showing 0 trains the eye to ignore the badge. */}
                        {Boolean(badges[item.to]) && (
                          <span className="ml-auto border border-warn/30 bg-warn/8 px-1.5 py-0.5 text-[11px] tabular-nums text-warn">
                            {badges[item.to]}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
  );
}

export function Layout() {
  const { user, role, logout } = useAuth();
  /* Polled by the query itself. The rail is the one place an unanswered ticket
     is visible from a page that is about something else entirely. */
  const supportCounts = useSupportCounts();
  const pendingInference = usePendingInferenceRequests();
  const pendingQuota = usePendingQuotaRequests();
  const openCases = useOpenRiskCases();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  function openDrawer() {
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : menuButtonRef.current;
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
  }

  function openSearch() {
    if (!drawerOpen) {
      window.dispatchEvent(new CustomEvent("palette:open"));
      return;
    }

    // The palette is the next modal surface. Let the drawer finish closing
    // before it opens, so Escape always affects only the surface on top.
    returnFocusRef.current = null;
    setDrawerOpen(false);
    requestAnimationFrame(() => window.dispatchEvent(new CustomEvent("palette:open")));
  }

  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // The mobile drawer is hidden at lg. Close its modal behavior as well when a
  // device rotates or a desktop window grows past that breakpoint.
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const onChange = () => {
      if (!desktop.matches) return;
      returnFocusRef.current = null;
      setDrawerOpen(false);
    };

    desktop.addEventListener("change", onChange);
    return () => desktop.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const getFocusable = () => {
      const drawer = drawerRef.current;
      if (!drawer) return [];

      return Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.tabIndex >= 0);
    };

    const frame = requestAnimationFrame(() => {
      const initialFocus = drawerRef.current?.querySelector<HTMLElement>("[data-drawer-initial-focus]");
      (initialFocus ?? getFocusable()[0])?.focus();
    });

    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        event.stopImmediatePropagation();
        openSearch();
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = getFocusable();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    window.addEventListener("keydown", onKey, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = previousOverflow;

      const returnFocus = returnFocusRef.current;
      if (returnFocus?.isConnected) returnFocus.focus();
      returnFocusRef.current = null;
    };
  }, [drawerOpen]);

  /*
   * The rail is dark in both colour schemes. Combined with the amber accent it
   * is the fastest possible signal that you are in the operator console looking
   * at somebody else's data — see index.css for why that matters.
   */
  const sidebar = (
    <div className="flex h-full flex-col gap-6 p-4 text-fg-muted">
      <div className="flex items-center justify-between">
        <NavLink to="/" className="flex items-center gap-2.5 pointer-coarse:min-h-11">
          <OpsMark />
          <span className="leading-tight">
            <span className="block font-semibold tracking-tight text-fg">Maxlayer</span>
            <span className="block text-[0.7rem] font-medium tracking-[0.14em] text-accent uppercase">
              Operations
            </span>
          </span>
        </NavLink>

        <button
          type="button"
          onClick={closeDrawer}
          data-drawer-initial-focus
          className="-mr-1 grid size-11 place-items-center text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg lg:hidden"
        >
          <span className="sr-only">Close navigation</span>
          <Icon name="close" className="size-5" />
        </button>
      </div>

      {/* A shortcut nobody knows about helps nobody, so the rail advertises it
          and the button opens the same palette for anyone not reaching for it. */}
      <button
        type="button"
        onClick={openSearch}
        className="flex items-center gap-2.5 pointer-coarse:min-h-11 border border-line px-3 py-2 text-sm text-fg-muted transition-colors duration-200 ease-smooth hover:border-line-strong hover:text-fg"
      >
        <Icon name="search" className="size-4" />
        Search
        <kbd className="ml-auto font-mono text-[0.6875rem] text-fg-subtle">⌘K</kbd>
      </button>

      <NavRail
        badges={{
          "/support": supportCounts.data?.open,
          /*
           * Somebody registered, confirmed their address and asked to join, and
           * is now waiting on a person. Without this the queue is invisible
           * from everywhere else in the console and stalls quietly — which is
           * the failure the affiliate payouts already have, approved by reading
           * rows out of Postgres.
           */
          "/free-inference": pendingInference.data,
          /*
           * Organizations asking to create more than their ceiling allows.
           * The queue lives under Organizations rather than as a fifth row
           * in the group -- four is where a heading stops predicting its
           * contents, and the rail's own test says so -- so the badge sits
           * on Organizations, and the page opens with the queue's banner.
           */
          "/organizations": pendingQuota.data,
          // Organizations waiting on a reviewer's decision.
          "/safety": openCases.data,
        }}
      />

      <div className="border-t border-line pt-4">
        <p className="truncate px-1 text-sm text-fg">{user?.name}</p>
        <p className="truncate px-1 text-xs text-fg-subtle">{user?.email}</p>
        {role && (
          <div className="mt-2.5 px-1">
            <PlatformRoleBadge role={role} />
          </div>
        )}
        <Link
          to="/security"
          className="mt-2.5 flex items-center gap-2 px-1 text-sm text-fg-muted transition-colors duration-200 ease-smooth hover:text-fg"
        >
          <Icon name="shield" className="size-4" />
          Account security
        </Link>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void logout()}
          className="mt-3 w-full justify-start"
        >
          <Icon name="logout" className="size-4" />
          Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-full">
      <CommandPalette />

      {/* Fixed rather than sticky so a long table scrolls under a stationary
          rail instead of dragging it along. The background lives here rather
          than on the inner div so the rail reads as a surface with an edge,
          which is also how the customer console builds it. */}
      <aside className="fixed inset-y-0 left-0 hidden w-60 overflow-y-auto border-r border-line bg-surface lg:block">
        {sidebar}
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={closeDrawer}
            className="absolute inset-0 bg-canvas/70"
          />
          <aside
            ref={drawerRef}
            id="mobile-navigation"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            className="absolute inset-y-0 left-0 w-72 max-w-[calc(100vw-2rem)] overflow-y-auto border-r border-line bg-surface"
          >
            {sidebar}
          </aside>
        </div>
      )}

      <div className="lg:pl-60">
        {/* Positioned inside the padding, not on the padded column — the rail
            is `fixed` with no z-index, and a positioned column later in the
            DOM hit-tests above it, killing its clicks. Same fix as the
            customer dashboard. */}
        <div className="relative isolate">
        {/* The shell's light, reading the console's own amber. See
            `shell-light` in index.css. */}
        <div aria-hidden className="shell-light pointer-events-none absolute inset-x-0 top-0 -z-10 h-80" />
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-surface/90 px-4 py-3 backdrop-blur lg:hidden">
          <button
            ref={menuButtonRef}
            type="button"
            onClick={openDrawer}
            aria-expanded={drawerOpen}
            aria-controls="mobile-navigation"
            /* 44px square. `p-2` around a 20px icon is 36, which is fine under a
               mouse and misses under a thumb — and this is the only way to reach
               navigation on a phone. It sits alone at the head of the bar, so
               there is no neighbouring target to steal a tap from. */
            className="-ml-1 grid size-11 place-items-center text-fg-muted transition-colors duration-200 ease-smooth hover:text-fg"
          >
            <span className="sr-only">Open navigation</span>
            <Icon name="menu" className="size-5" />
          </button>

          <div className="flex items-center gap-2">
            <OpsMark className="size-6" />
            <span className="font-semibold tracking-tight">Maxlayer Ops</span>
          </div>

          {role && <span className="ml-auto">{<PlatformRoleBadge role={role} />}</span>}
        </header>

        {/* Standing reminder that everything here is somebody else's data. */}
        {role === "support" && (
          <div className="flex items-center justify-center gap-2 border-b border-warn/30 bg-warn/8 px-4 py-2 text-xs text-warn">
            <Icon name="shield" className="size-3.5" />
            Read-only session — support staff cannot modify tenant data.
          </div>
        )}

        <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
          <Outlet />
        </main>
        </div>
      </div>
    </div>
  );
}
